import classnames from 'classnames'
import { TFunction } from 'i18next'
import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './D3.module.scss'

interface Chart<DATA> {
  destroy(): void
  updateData(data: DATA): void
  setLocale(lng: string, t: TFunction): void
}

export default function createD3Component<DATA>(
  factory: ($container: HTMLDivElement) => Chart<DATA>,
  defaultProps?: React.HTMLProps<HTMLDivElement>
) {
  const { className: defaultClassName, ...otherDefaultProps } = defaultProps || {}

  function D3({ data, className, ...props }: { data: DATA } & Omit<React.HTMLProps<HTMLDivElement>, 'data'>) {
    const { i18n, t } = useTranslation()

    const chartRef = useRef<Chart<DATA> | null>(null)

    // Create chart once the div has been created
    const containerRef = useCallback(
      ($container: HTMLDivElement) => {
        if ($container) {
          if (chartRef.current) {
            chartRef.current.destroy()
          }
          chartRef.current = factory($container)
        }
      },
      [chartRef]
    )

    // Update data when it changes
    useEffect(() => {
      chartRef.current && chartRef.current.updateData(data)
    }, [chartRef.current, data])

    // Update language when it changes
    useEffect(() => {
      chartRef.current && chartRef.current.setLocale(i18n.language, t)
    }, [i18n.language, t])

    return (
      <div
        ref={containerRef}
        className={classnames(styles.D3, className, defaultClassName)}
        {...otherDefaultProps}
        {...props}
      />
    )
  }

  return React.memo(D3)
}
